import { useState, type ReactNode } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import { productThumbSrc } from '@modules/products/utils';
import {
  useOrder,
  useOrderStatusHistory,
  OrderStatus as OrderStatusEnum,
  getStatusLabel,
  type Order,
  type OrderItem,
  type RecipientData,
  type AssemblySlotData,
  formatTimeSlot,
} from '@modules/orders';
import { OrderStatus, OrderPayStatus, OrderPaymentMethod, OrderActions } from '@modules/orders/components';
import {
  deliveryApi,
  DeliveryStatusTag,
  type DostavistaDelivery,
  type DostavistaOrderPoint,
} from '@modules/delivery';
import { Breadcrumbs } from '@components';
import { BackButton, CachedImage, RefetchButton, Button, DoubleCheckModal, ErrorModal, QuietIconButton, Tooltip } from '@shared/ui';
import { formatDate, formatDvIntervalLine, formatPrice, formatPhoneNumber, getTimezoneByCity } from '@shared/utils';
import { DOSTAVISTA_PARAMETER_ERROR_DESCRIPTIONS } from '@shared/constants/dostavista-errors';
import '@shared/styles/page.css';
import './OrderDetailsPage.css';

function orderItemThumbSrc(product: {
  images?: Array<{ url?: string | null; cardUrl?: string | null }> | string | null;
  imageUrl?: string | null;
  cardUrl?: string | null;
} | null | undefined): string | null {
  if (!product) return null;
  if (Array.isArray(product.images) && product.images.length > 0) {
    return productThumbSrc(product.images[0]);
  }
  if (product.cardUrl) return product.cardUrl;
  if (product.imageUrl) return product.imageUrl;
  if (typeof product.images === 'string' && product.images) return product.images;
  return null;
}

const getRecipient = (recipientData: RecipientData | null | undefined): string => {
  if (!recipientData) return 'Не указано';
  const parts = [];
  if (recipientData.firstName) parts.push(recipientData.firstName);
  if (recipientData.lastName) parts.push(recipientData.lastName);
  return parts.length > 0 ? parts.join(' ') : 'Не указано';
};

const getPickupPointAddress = (order: Order): string => {
  const data = order.pickupPointData;
  if (!data) return '-';
  const parts = [data.city, data.address].filter(Boolean);
  if (data.metro) parts.push(`м. ${data.metro}`);
  return parts.length > 0 ? parts.join(', ') : '-';
};

/** Откуда курьер забирает заказ: сначала точка из ответа Достависты, иначе адрес ПВЗ из заказа */
function getPickupSourceAddressLine(order: Order, firstDv: DostavistaDelivery | undefined): string {
  const pp = firstDv?.pickupPoint;
  if (pp) {
    const parts = [pp.city, pp.address].filter(Boolean);
    if (pp.metro) parts.push(`м. ${pp.metro}`);
    if (parts.length > 0) return parts.join(', ');
  }
  const fromOrder = getPickupPointAddress(order);
  return fromOrder !== '-' ? fromOrder : '—';
}

type DvPointTimeParts = {
  planned: string | null;
  actual: string | null;
  actualKind: 'visit' | 'eta' | null;
};

function formatDvPointTimeParts(
  d: DostavistaDelivery,
  point: DostavistaOrderPoint | null | undefined,
  timeZone: string,
): DvPointTimeParts {
  const rs = point?.required_start_datetime ?? d.required_start_datetime;
  const rf = point?.required_finish_datetime ?? d.required_finish_datetime;
  let planned: string | null = null;
  if (rs && rf) planned = formatDvIntervalLine(rs, rf, timeZone);
  else if (rs) planned = formatDate(rs, timeZone);

  const visit =
    point?.courier_visit_datetime ?? d.courier_visit_datetime;
  const eta =
    point?.estimated_arrival_datetime ?? d.estimated_arrival_datetime;
  const arrivalStart = point?.arrival_start_datetime;
  const arrivalFinish = point?.arrival_finish_datetime;
  if (visit) {
    return {
      planned,
      actual: formatDate(visit, timeZone),
      actualKind: 'visit',
    };
  }
  if (eta) {
    return {
      planned,
      actual: formatDate(eta, timeZone),
      actualKind: 'eta',
    };
  }
  if (arrivalStart && arrivalFinish) {
    return {
      planned,
      actual: formatDvIntervalLine(arrivalStart, arrivalFinish, timeZone),
      actualKind: 'eta',
    };
  }
  if (arrivalStart) {
    return {
      planned,
      actual: formatDate(arrivalStart, timeZone),
      actualKind: 'eta',
    };
  }
  return { planned, actual: null, actualKind: null };
}

function renderDvPointTime(parts: DvPointTimeParts): ReactNode {
  if (!parts.planned && !parts.actual) return '—';
  return (
    <div className="order-dostavista-time-stack">
      {parts.planned ? (
        <div className="order-dostavista-time-line">
          <span className="order-dostavista-time-label">План</span>
          <span>{parts.planned}</span>
        </div>
      ) : null}
      {parts.actual ? (
        <div className="order-dostavista-time-line">
          <span className="order-dostavista-time-label">
            {parts.actualKind === 'visit' ? 'Факт' : 'Ожид.'}
          </span>
          <span>{parts.actual}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Время прибытия на точку забора: окно из создания + ETA/факт от Достависты, если есть. */
function formatDvPickupPointTimeParts(
  rows: DostavistaDelivery[],
  template: DostavistaDelivery | undefined,
  timeZone: string,
): DvPointTimeParts {
  const pickupPoint = template?.dostavistaOrder?.points?.[0];
  if (!pickupPoint || !template) {
    return { planned: null, actual: null, actualKind: null };
  }
  const leg =
    pickupPoint.delivery_id != null
      ? rows.find((r) => r.delivery_id === pickupPoint.delivery_id)
      : undefined;
  return formatDvPointTimeParts(leg ?? template, pickupPoint, timeZone);
}

function getCourierLine(d: DostavistaDelivery): string {
  const c = d.courier;
  if (!c?.phone) return '—';
  const phoneFormatted = formatPhoneNumber(c.phone) || c.phone;
  const name = [c.surname, c.name, c.middlename].filter(Boolean).join(' ').trim();
  return name ? `${phoneFormatted} (${name})` : phoneFormatted;
}

function getDostavistaLegAddresses(
  order: Order,
  d: DostavistaDelivery,
  points: DostavistaOrderPoint[],
  pointIdx: number,
  point: DostavistaOrderPoint | null,
): { from: string; to: string } {
  const to = (point?.address ?? d.address)?.trim() || '—';
  if (pointIdx > 0) {
    const from = points[pointIdx - 1]?.address?.trim() || '—';
    return { from, to };
  }
  return {
    from: getPickupSourceAddressLine(order, d),
    to,
  };
}

function getDostavistaLegTimes(
  rows: DostavistaDelivery[],
  d: DostavistaDelivery,
  points: DostavistaOrderPoint[],
  pointIdx: number,
  point: DostavistaOrderPoint | null | undefined,
  timeZone: string,
): { from: DvPointTimeParts; to: DvPointTimeParts } {
  const timeTo = formatDvPointTimeParts(d, point ?? undefined, timeZone);
  if (pointIdx > 0) {
    const fp = points[pointIdx - 1];
    const fromRow =
      fp?.delivery_id != null ? rows.find((r) => r.delivery_id === fp.delivery_id) : undefined;
    return {
      from: formatDvPointTimeParts(fromRow ?? d, fp, timeZone),
      to: timeTo,
    };
  }
  return {
    from: formatDvPickupPointTimeParts(rows, d, timeZone),
    to: timeTo,
  };
}

const getDeliveryDetails = (order: Order): string => {
  const addr = order.addressData;
  if (!addr) return '—';
  const parts: string[] = [];
  if (addr.entrance) parts.push(`подъезд ${addr.entrance}`);
  if (addr.floor) parts.push(`этаж ${addr.floor}`);
  if (addr.apartment) parts.push(`кв. ${addr.apartment}`);
  if (addr.intercom) parts.push(`домофон ${addr.intercom}`);
  return parts.length > 0 ? parts.join(', ') : '—';
};

const getOrderComment = (order: Order): string => {
  const parts = [order.orderComment, order.notes]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean);
  return [...new Set(parts)].join(' · ') || '—';
};

const RowLabel = ({ children }: { children: ReactNode }) => (
  <span className="detail-row__label">{children}</span>
);

const TimeSlotTag = ({ assemblySlotData }: { assemblySlotData: AssemblySlotData | null | undefined }) => (
  <span className="time-slot-tag">{formatTimeSlot(assemblySlotData)}</span>
);

const changedByLabels: Record<string, string> = {
  ADMIN: 'Админ',
  USER: 'Пользователь',
  SYSTEM: 'Система',
  COURIER: 'Курьер',
};

function collectDostavistaParameterErrorLines(
  value: unknown,
  path = '',
): Array<{ path: string; code: string; description: string }> {
  if (Array.isArray(value)) {
    const allStrings = value.every((v) => typeof v === 'string');
    if (allStrings) {
      return (value as string[]).map((code) => ({
        path: path || '(root)',
        code,
        description:
          DOSTAVISTA_PARAMETER_ERROR_DESCRIPTIONS[code] ??
          'Неизвестный код ошибки параметра.',
      }));
    }
    return value.flatMap((v, index) => {
      if (v == null) return [];
      const nextPath = path ? `${path}[${index}]` : `[${index}]`;
      return collectDostavistaParameterErrorLines(v, nextPath);
    });
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) =>
      collectDostavistaParameterErrorLines(v, path ? `${path}.${k}` : k),
    );
  }
  return [];
}

function normalizeDostavistaParameterErrors(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

/** Статусы заказа Достависты, при которых доступен «Заказать возврат» */
const EDITABLE_ORDER_STATUSES = ['new', 'available', 'active', 'delayed'] as const;

/** Возврат уже заказан: вторая точка помечена возвратной или совпадает с первой. Статус заказа при этом не меняется. */
function isDostavistaReturnAlreadyOrdered(
  points: Array<{ address?: string | null; is_return_point?: unknown }> | null | undefined,
): boolean {
  if (!points || points.length < 2) return false;
  if (points.some((point) => point.is_return_point === true)) return true;
  const first = (points[0]?.address ?? '').trim().toLowerCase();
  const second = (points[1]?.address ?? '').trim().toLowerCase();
  return first.length > 0 && first === second;
}

function isSellerOwnDelivery(order: Order | undefined): boolean {
  if (!order) return false;
  const snap = order.pickupPointData?.deliveryProvider;
  if (snap === 'SELLER') return true;
  if (snap === 'MARKETPLACE') return false;
  return order.pickupPointShop?.deliveryProvider === 'SELLER';
}

export default function OrderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const orderId = id ? parseInt(id, 10) : 0;
  const [cancelModalOrderId, setCancelModalOrderId] = useState<number | null>(null);
  const [cancelModalOrderNumber, setCancelModalOrderNumber] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  /** Штраф из ответа cancel-order (пока refetch не подтянул late_cancellation_penalty_amount) */
  const [cancelPenaltyByOrderId, setCancelPenaltyByOrderId] = useState<Record<number, number>>({});
  const [orderCannotBeCanceledModalOpen, setOrderCannotBeCanceledModalOpen] = useState(false);
  const [returnLoadingOrderId, setReturnLoadingOrderId] = useState<number | null>(null);
  const [returnModalOrderId, setReturnModalOrderId] = useState<number | null>(null);
  const { data: order, isLoading, error, isFetching, refetch } = useOrder(orderId);
  const { data: statusHistory, isLoading: historyLoading } = useOrderStatusHistory(orderId, true);

  const { data: dostavistaListResponse, isLoading: dostavistaListLoading, refetch: refetchDostavista } = useQuery({
    queryKey: ['delivery', 'admin-order-details', order?.id, order?.orderNumber],
    queryFn: () => deliveryApi.getDeliveries({ search_text: order!.orderNumber, count: 100 }),
    enabled:
      !!order?.orderNumber &&
      !isSellerOwnDelivery(order) &&
      (order.deliveryMethod === 'DELIVERY' ||
        (order.dostavistaOrderId != null && order.dostavistaOrderId > 0) ||
        !!order.dostavistaCreationError),
  });

  const dostavistaForOrder =
    order != null
      ? (dostavistaListResponse?.deliveries ?? []).filter((d) => d.order?.id === order.id)
      : [];

  const firstDv = dostavistaForOrder[0];
  const dostavistaRouteOrderId = firstDv?.dostavistaOrder?.order_id ?? firstDv?.order_id ?? null;
  const dostavistaRouteStatusRaw = firstDv?.dostavistaOrder?.status ?? null;
  const dostavistaRouteStatus =
    dostavistaRouteStatusRaw != null ? String(dostavistaRouteStatusRaw).trim().toLowerCase() : null;
  const returnAlreadyOrdered = isDostavistaReturnAlreadyOrdered(firstDv?.dostavistaOrder?.points);
  const canOrderReturn =
    dostavistaRouteOrderId != null &&
    returnLoadingOrderId !== dostavistaRouteOrderId &&
    dostavistaRouteStatus != null &&
    (EDITABLE_ORDER_STATUSES as readonly string[]).includes(dostavistaRouteStatus) &&
    !returnAlreadyOrdered;

  // Cancel в API Достависты только для new/available/active/delayed — не для canceled/completed.
  const showDostavistaCancel =
    dostavistaRouteOrderId != null &&
    dostavistaRouteStatus != null &&
    (EDITABLE_ORDER_STATUSES as readonly string[]).includes(dostavistaRouteStatus);

  const pickupCity =
    order?.pickupPointData?.city ??
    order?.pickupPointShop?.city ??
    null;
  const pickupTz =
    order?.pickupPointData?.timezone &&
    order.pickupPointData.timezone.includes('/')
      ? order.pickupPointData.timezone
      : getTimezoneByCity(pickupCity);

  const openCancelModal = () => {
    if (!order) return;
    setCancelModalOrderId(order.id);
    setCancelModalOrderNumber(order.orderNumber);
  };

  const closeCancelModal = () => {
    if (!cancelLoading) {
      setCancelModalOrderId(null);
      setCancelModalOrderNumber(null);
    }
  };

  const confirmCancelDelivery = async () => {
    if (cancelModalOrderId == null) return;
    setCancelLoading(true);
    try {
      const cancelRes = await deliveryApi.cancelOrder(cancelModalOrderId);
      const penaltyRaw = cancelRes.order?.late_cancellation_penalty_amount;
      const penaltyNum = parseFloat(String(penaltyRaw ?? '0'));
      const hasPenalty = Number.isFinite(penaltyNum) && penaltyNum > 0;
      if (hasPenalty) {
        setCancelPenaltyByOrderId((prev) => ({
          ...prev,
          [cancelModalOrderId]: penaltyNum,
        }));
      }
      notifications.show({
        title: 'Готово',
        message: hasPenalty
          ? `Заказ доставки в Достависте отменён. Штраф: ${formatPrice(penaltyNum)}`
          : 'Заказ доставки в Достависте отменён.',
        color: 'green',
      });
      setCancelModalOrderId(null);
      setCancelModalOrderNumber(null);
      await refetchDostavista();
      queryClient.invalidateQueries({ queryKey: ['delivery'] });
      refetch?.();
    } catch (err: unknown) {
      const msg = String(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        '',
      );
      if (msg.includes('order_cannot_be_canceled')) {
        setCancelModalOrderId(null);
        setCancelModalOrderNumber(null);
        setOrderCannotBeCanceledModalOpen(true);
      } else {
        notifications.show({
          title: 'Ошибка',
          message: msg || 'Не удалось отменить заказ доставки.',
          color: 'red',
        });
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const closeReturnModal = () => {
    if (returnLoadingOrderId == null) {
      setReturnModalOrderId(null);
    }
  };

  const confirmOrderReturn = async () => {
    if (returnModalOrderId == null) return;
    setReturnLoadingOrderId(returnModalOrderId);
    try {
      await deliveryApi.orderReturn(returnModalOrderId);
      notifications.show({
        title: 'Готово',
        message: 'В заказе Достависты включён возврат на первую точку.',
        color: 'green',
      });
      setReturnModalOrderId(null);
      await refetchDostavista();
      queryClient.invalidateQueries({ queryKey: ['delivery'] });
      refetch?.();
    } catch (err: unknown) {
      const msg = String(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error)?.message ??
        '',
      );
      notifications.show({
        title: 'Ошибка',
        message: msg || 'Не удалось заказать возврат.',
        color: 'red',
      });
    } finally {
      setReturnLoadingOrderId(null);
    }
  };

  const getContent = () => {
    if (isLoading || isFetching) {
      return (
        <div className="content-wrapper content-wrapper--centered">
          <Loader />
        </div>
      );
    }

    if (error && refetch) {
      return (
        <div className="content-wrapper">
          <RefetchButton
            refetch={refetch}
            isLoading={isLoading || isFetching}
          />
        </div>
      );
    }

    return (
      null
    );
  };

  if (!order) return getContent()


  const handleBack = () => {
    if (location.state?.from === 'seller-finance') {
      navigate('/seller-finance', { state: { tab: 'platform' } });
      return;
    }
    if (location.state?.from === 'seller') {
      const sellerId = Number(location.state?.sellerId);
      if (Number.isFinite(sellerId) && sellerId > 0) {
        navigate(`/sellers/${sellerId}`, { state: { tab: 'finance' } });
        return;
      }
    }
    if (location.state?.from === 'reviews') {
      navigate('/reviews');
      return;
    }
    if (location.state?.from === 'client-support-chat') {
      const chatUserId = location.state?.userId;
      navigate(
        chatUserId != null && Number(chatUserId) > 0
          ? `/clients/support-chat?userId=${chatUserId}`
          : '/clients/support-chat',
      );
      return;
    }
    navigate('/orders');
  };

  const senderName = order.user
    ? [order.user.name, order.user.surname].filter(Boolean).join(' ') || 'Не указано'
    : 'Не указано';
  const senderPhone = order.user?.phone ? formatPhoneNumber(order.user.phone) : 'Не указано';
  const dostavistaParameterErrorsRawDirect = (
    order as unknown as { dostavistaCreationParameterErrors?: unknown }
  ).dostavistaCreationParameterErrors;
  const dostavistaParameterErrorsRaw = normalizeDostavistaParameterErrors(
    dostavistaParameterErrorsRawDirect ??
    (order as unknown as { parameterErrors?: unknown }).parameterErrors ??
    null,
  );
  const hasDostavistaParameterErrors =
    dostavistaParameterErrorsRaw !== null &&
    dostavistaParameterErrorsRaw !== undefined &&
    !(typeof dostavistaParameterErrorsRaw === 'string' &&
      dostavistaParameterErrorsRaw.trim() === '');
  const dostavistaParameterErrorLines = collectDostavistaParameterErrorLines(
    dostavistaParameterErrorsRaw,
  );

  return (
    <div className="page-container order-details-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Заказы', path: '/orders' },
          { label: order.orderNumber },
        ]}
      />

      <div className="page-header">
        <div className="page-header-top">
          <div className="order-details-page__title-row">
            <BackButton onClick={handleBack} />
            <h1>Заказ {order.orderNumber}</h1>
            <div className="order-details-page__header-meta">
              <OrderStatus status={order.status} />
              <TimeSlotTag assemblySlotData={order.assemblySlotData} />
            </div>
          </div>
          {!getContent() && (
            <div className="order-details-page__header-actions">
              <OrderActions orderId={order.id} status={order.status} deliveryMethod={order.deliveryMethod} />
            </div>
          )}
        </div>
      </div>

      {getContent() ? getContent() : (
        <div className="content-wrapper">
          <div className="order-details">
            {((order.items && order.items.length > 0) || (order.readyImages && order.readyImages.length > 0)) && (
              <div className="order-details-primary">
                {order.items && order.items.length > 0 && (
                  <section className="order-details-card order-details-primary__main">
                    <h2 className="order-details-card__title">Состав заказа</h2>
                    <div className="order-items-list">
                      {order.items.map((item: OrderItem) => {
                        const product = item.product || (typeof item.productData === 'object' ? item.productData : null);
                        const productName = product?.name || 'Товар';
                        const pd = typeof item.productData === 'object' ? item.productData : null;
                        const hasDiscount = !!pd?.discountedPrice;
                        const originalPrice = hasDiscount && pd?.price != null
                          ? (typeof pd.price === 'number' ? pd.price : parseFloat(String(pd.price)))
                          : null;

                        const thumbSrc = orderItemThumbSrc(product);
                        const productId = product?.id || item.productId;

                        return (
                          <Link
                            key={item.id}
                            to={`/products/${productId}`}
                            state={{ from: 'order', orderId: order.id }}
                            className="order-item"
                          >
                            {thumbSrc ? (
                              <div className="order-item-image">
                                <CachedImage
                                  src={thumbSrc}
                                  alt={productName}
                                  loading="lazy"
                                  decoding="async"
                                />
                              </div>
                            ) : (
                              <div className="order-item-image order-item-image-placeholder" aria-hidden />
                            )}
                            <div className="order-item-info">
                              <p className="order-item-name">{productName}</p>
                              <div className="order-item-details">
                                <span>× {item.quantity}</span>
                                <span className="order-item-details__sep" aria-hidden>·</span>
                                {hasDiscount && originalPrice != null && !Number.isNaN(originalPrice) ? (
                                  <>
                                    <span className="order-item-details__strike">{formatPrice(originalPrice)}</span>
                                    <span>{formatPrice(item.price)}</span>
                                  </>
                                ) : (
                                  <span>{formatPrice(item.price)}</span>
                                )}
                              </div>
                              <div className="order-item-total">{formatPrice(item.total)}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                )}

                {order.readyImages && order.readyImages.length > 0 && (
                  <section className="order-details-card order-details-primary__side">
                    <div className="order-details-card__title-row">
                      <h2 className="order-details-card__title">Фото готового букета</h2>
                      {order.readyImagesApproved === true ? (
                        <span className="order-details-flag-tag order-details-flag-tag--ok">Принят</span>
                      ) : order.readyImagesApproved === false ? (
                        <span className="order-details-flag-tag order-details-flag-tag--bad">Отклонён</span>
                      ) : null}
                    </div>
                    <div className="order-ready-images">
                      {order.readyImages.map((imageUrl, index) => (
                        <div key={index} className="order-ready-image-item">
                          <CachedImage src={imageUrl} alt={`Готовый букет ${index + 1}`} loading="lazy" />
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}

            <div className="order-details-secondary">
              <div className="order-details-secondary__main">
                {order.pickupPointShop ? (
                  <section className="order-details-card">
                    <div className="order-details-card__title-row">
                      <h2 className="order-details-card__title">Точка сборки и магазин</h2>
                      {order.pickupPointShop.seller ? (
                        <Tooltip label="Перейти в чат">
                          <QuietIconButton
                            icon={<IoChatbubbleEllipsesOutline size={18} />}
                            aria-label="Перейти в чат"
                            onClick={() => {
                              const seller = order.pickupPointShop!.seller!;
                              const params = new URLSearchParams({
                                sellerId: String(seller.id),
                              });
                              if (seller.shopName) {
                                params.set('shopName', seller.shopName);
                              }
                              navigate(`/sellers/support-chat?${params.toString()}`);
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </div>
                    <div className="detail-row">
                      <RowLabel>Адрес точки</RowLabel>
                      <span className="detail-row__value">
                        {order.pickupPointShop.city}, {order.pickupPointShop.address}
                        {order.pickupPointShop.metro ? ` · м. ${order.pickupPointShop.metro}` : ''}
                      </span>
                    </div>
                    {(order.pickupPointShop.phoneNumber ||
                      order.pickupPointShop.email ||
                      order.pickupPointShop.telegram) && (
                        <div className="detail-row">
                          <RowLabel>Контакты точки</RowLabel>
                          <span className="detail-row__value">
                            {[
                              order.pickupPointShop.phoneNumber
                                ? formatPhoneNumber(order.pickupPointShop.phoneNumber)
                                : null,
                              order.pickupPointShop.email ?? null,
                              order.pickupPointShop.telegram ? `Telegram: ${order.pickupPointShop.telegram}` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </span>
                        </div>
                      )}
                    <div className="detail-row">
                      <RowLabel>Магазин</RowLabel>
                      <span className="detail-row__value">
                        {order.pickupPointShop.seller ? (
                          <Link
                            to={`/sellers/${order.pickupPointShop.seller.id}`}
                            state={{ from: 'order', orderId: order.id }}
                          >
                            {order.pickupPointShop.seller.shopName}
                          </Link>
                        ) : (
                          'Точка маркетплейса (без привязки к магазину продавца)'
                        )}
                      </span>
                    </div>
                  </section>
                ) : null}

                {order.deliveryMethod === 'DELIVERY' && (
                  <section className="order-details-card">
                    <div className="order-details-card__title-row">
                      <h2 className="order-details-card__title">Доставка</h2>
                      <div className="order-details-delivery-tags">
                        {order.deliverAnonymously ? (
                          <span className="order-details-flag-tag">Анонимно</span>
                        ) : null}
                        {order.callBeforeDelivery ? (
                          <span className="order-details-flag-tag">Позвонить</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Адрес</RowLabel>
                      <span className="detail-row__value">{order.addressData?.address || '—'}</span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Детали</RowLabel>
                      <span className="detail-row__value">{getDeliveryDetails(order)}</span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Получатель</RowLabel>
                      <span className="detail-row__value">
                        {formatPhoneNumber(order.recipientData?.phone)} ({getRecipient(order.recipientData)})
                      </span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Отправитель</RowLabel>
                      <span className="detail-row__value">{senderPhone} ({senderName})</span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Комментарий к заказу</RowLabel>
                      <span className="detail-row__value">{getOrderComment(order)}</span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Комментарий курьеру</RowLabel>
                      <span className="detail-row__value">{order.addressData?.comment || '—'}</span>
                    </div>

                    {!isSellerOwnDelivery(order) &&
                      (dostavistaListLoading ||
                        dostavistaForOrder.length > 0 ||
                        (order.dostavistaOrderId != null && order.dostavistaOrderId > 0) ||
                        !!order.dostavistaCreationError ||
                        hasDostavistaParameterErrors) && (
                        <div className="order-details-delivery-dostavista">
                          {order.dostavistaCreationError ? (
                            <p className="order-dostavista-error">
                              Ошибка создания доставки: {order.dostavistaCreationError}
                            </p>
                          ) : null}
                          {hasDostavistaParameterErrors ? (
                            dostavistaParameterErrorLines.length > 0 ? (
                              dostavistaParameterErrorLines.map((line, idx) => (
                                <p
                                  key={`${line.path}-${line.code}-${idx}`}
                                  className="order-dostavista-error-line"
                                >
                                  {line.path} — {line.code} ({line.description})
                                </p>
                              ))
                            ) : (
                              <p className="order-dostavista-error-line">
                                Не удалось разобрать структуру ошибок параметров.
                              </p>
                            )
                          ) : null}
                          {dostavistaListLoading ? (
                            <Loader size="sm" />
                          ) : dostavistaForOrder.length > 0 ? (
                            <div className="order-dostavista-list">
                              {dostavistaForOrder.map((d, cardIndex) => {
                                const points = d.dostavistaOrder?.points ?? [];
                                const pointIdx = points.findIndex((p) => p.delivery_id === d.delivery_id);
                                const point = pointIdx >= 0 ? points[pointIdx] : null;
                                const { from: addrFrom, to: addrTo } = getDostavistaLegAddresses(
                                  order,
                                  d,
                                  points,
                                  pointIdx,
                                  point,
                                );
                                const { from: timeFrom, to: timeTo } = getDostavistaLegTimes(
                                  dostavistaForOrder,
                                  d,
                                  points,
                                  pointIdx,
                                  point,
                                  pickupTz,
                                );
                                const dvOrderId =
                                  d.dostavistaOrder?.order_id ?? order.dostavistaOrderId ?? null;
                                const penaltyFromApi = parseFloat(
                                  String(d.dostavistaOrder?.late_cancellation_penalty_amount ?? '0'),
                                );
                                const penaltyFromCancel = cancelPenaltyByOrderId[order.id];
                                const lateCancelPenalty =
                                  Number.isFinite(penaltyFromApi) && penaltyFromApi > 0
                                    ? penaltyFromApi
                                    : penaltyFromCancel != null && penaltyFromCancel > 0
                                      ? penaltyFromCancel
                                      : null;
                                return (
                                  <div
                                    key={`${d.delivery_id}-${d.order_id ?? ''}`}
                                    className="order-dostavista-card"
                                  >
                                    <div className="order-dostavista-card-head">
                                      <div className="order-dostavista-card-head-main">
                                        {dvOrderId != null && dvOrderId > 0 ? (
                                          <span className="order-dostavista-id">
                                            Достависта id: {dvOrderId}
                                          </span>
                                        ) : null}
                                        <DeliveryStatusTag status={d.status} />
                                        {cardIndex === 0 && returnAlreadyOrdered ? (
                                          <span className="order-details-flag-tag order-details-flag-tag--return">Идёт возврат</span>
                                        ) : null}
                                        {cardIndex === 0 && lateCancelPenalty != null ? (
                                          <span className="order-dostavista-penalty">
                                            Штраф: {formatPrice(lateCancelPenalty)}
                                          </span>
                                        ) : null}
                                      </div>
                                      {cardIndex === 0 && (showDostavistaCancel || canOrderReturn) ? (
                                        <div className="order-dostavista-actions">
                                          {showDostavistaCancel ? (
                                            <Button variant="secondary" size="xs" onClick={openCancelModal}>
                                              Отменить
                                            </Button>
                                          ) : null}
                                          {canOrderReturn && dostavistaRouteOrderId != null ? (
                                            <Button
                                              variant="secondary"
                                              size="xs"
                                              loading={returnLoadingOrderId === dostavistaRouteOrderId}
                                              onClick={() => setReturnModalOrderId(dostavistaRouteOrderId)}
                                            >
                                              Возврат
                                            </Button>
                                          ) : null}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="order-dostavista-route" aria-label="Маршрут">
                                      <div className="order-dostavista-route-col">
                                        <div className="order-dostavista-addr">{addrFrom}</div>
                                        <div className="order-dostavista-time">
                                          {renderDvPointTime(timeFrom)}
                                        </div>
                                      </div>
                                      <div className="order-dostavista-route-arrow" aria-hidden>
                                        →
                                      </div>
                                      <div className="order-dostavista-route-col order-dostavista-route-col--end">
                                        <div className="order-dostavista-addr">{addrTo}</div>
                                        <div className="order-dostavista-time">
                                          {renderDvPointTime(timeTo)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="order-dostavista-meta">
                                      <div className="order-dostavista-meta-row">
                                        <span className="order-dostavista-meta-label">Курьер</span>
                                        <span className="order-dostavista-meta-value">
                                          {getCourierLine(d)}
                                        </span>
                                      </div>
                                      {d.delivery_price_amount != null ? (
                                        <div className="order-dostavista-meta-row">
                                          <span className="order-dostavista-meta-label">Стоимость</span>
                                          <span className="order-dostavista-meta-value order-dostavista-meta-value--strong">
                                            {formatPrice(parseFloat(String(d.delivery_price_amount)))}
                                          </span>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            !order.dostavistaCreationError && (
                              <div className="order-dostavista-empty">
                                <div className="order-dostavista-card-head-main">
                                  <span className="order-dostavista-id">Заказ № {order.orderNumber}</span>
                                  {order.dostavistaOrderId != null && order.dostavistaOrderId > 0 ? (
                                    <span className="order-dostavista-time">
                                      Достависта order_id: {order.dostavistaOrderId}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="order-dostavista-route order-dostavista-route--muted">
                                  <div className="order-dostavista-route-col">
                                    <div className="order-dostavista-addr">
                                      {getPickupSourceAddressLine(order, firstDv)}
                                    </div>
                                    <div className="order-dostavista-time">
                                      {renderDvPointTime(
                                        formatDvPickupPointTimeParts(
                                          dostavistaForOrder,
                                          firstDv,
                                          pickupTz,
                                        ),
                                      )}
                                    </div>
                                  </div>
                                  <div className="order-dostavista-route-arrow" aria-hidden>
                                    →
                                  </div>
                                  <div className="order-dostavista-route-col order-dostavista-route-col--end">
                                    <div className="order-dostavista-addr detail-row__value--muted">—</div>
                                    <div className="order-dostavista-time">—</div>
                                  </div>
                                </div>
                                <p className="order-dostavista-empty__hint">
                                  Данные по доставке в Достависте пока не найдены (поиск по номеру заказа).
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      )}
                  </section>
                )}

                {order.deliveryMethod === 'PICKUP' && (
                  <section className="order-details-card">
                    <h2 className="order-details-card__title">Самовывоз</h2>
                    <div className="detail-row">
                      <RowLabel>Адрес</RowLabel>
                      <span className="detail-row__value">{getPickupPointAddress(order)}</span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Получатель</RowLabel>
                      <span className="detail-row__value">
                        {formatPhoneNumber(order.recipientData?.phone)} ({getRecipient(order.recipientData)})
                      </span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Отправитель</RowLabel>
                      <span className="detail-row__value">{senderPhone} ({senderName})</span>
                    </div>
                    <div className="detail-row">
                      <RowLabel>Комментарий к заказу</RowLabel>
                      <span className="detail-row__value">{getOrderComment(order)}</span>
                    </div>
                  </section>
                )}
              </div>

              <div className="order-details-secondary__side">
                <section className="order-details-card order-details-card--payment">
                  <div className="order-details-card__title-row">
                    <h2 className="order-details-card__title">Оплата</h2>
                    <OrderPayStatus paymentStatus={order.paymentStatus} />
                  </div>
                  <div className="detail-row">
                    <RowLabel>Цена</RowLabel>
                    <span
                      className={`detail-row__value${(order.totalDiscount ?? 0) > 0 || (order.promoDiscountAmount ?? 0) > 0
                        ? ' detail-row__value--strike'
                        : ''
                        }`}
                    >
                      {formatPrice(order.subtotal)}
                    </span>
                  </div>
                  {(order.totalDiscount ?? 0) > 0 && (
                    <div className="detail-row">
                      <RowLabel>Скидка на товары</RowLabel>
                      <span className="detail-row__value">−{formatPrice(order.totalDiscount ?? 0)}</span>
                    </div>
                  )}
                  {(order.promoDiscountAmount ?? 0) > 0 && (
                    <div className="detail-row">
                      <RowLabel>Промокод</RowLabel>
                      <span className="detail-row__value">−{formatPrice(order.promoDiscountAmount ?? 0)}</span>
                    </div>
                  )}
                  {(order.deliveryAmount ?? 0) > 0 && (
                    <div className="detail-row">
                      <RowLabel>Доставка</RowLabel>
                      <span className="detail-row__value">{formatPrice(order.deliveryAmount ?? 0)}</span>
                    </div>
                  )}
                  {((order.totalDiscount ?? 0) > 0 || (order.promoDiscountAmount ?? 0) > 0) && (
                    <div className="detail-row">
                      <RowLabel>Итого</RowLabel>
                      <span className="detail-row__value detail-row__value--accent">
                        {formatPrice(order.totalAmount)}
                      </span>
                    </div>
                  )}
                  <div className="detail-row">
                    <RowLabel>Тип оплаты</RowLabel>
                    <span className="detail-row__value">
                      <OrderPaymentMethod paymentMethod={order.paymentMethod} />
                    </span>
                  </div>
                  <div className="detail-row">
                    <RowLabel>Бонусами</RowLabel>
                    <span className="detail-row__value">{formatPrice(order.bonusesUsed ?? 0)}</span>
                  </div>
                  <div className="detail-row">
                    <RowLabel>Начислено</RowLabel>
                    <span className="detail-row__value">{formatPrice(order.bonusesEarned ?? 0)}</span>
                  </div>
                </section>

                <section className="order-details-card">
                  <h2 className="order-details-card__title">История</h2>
                  <div className="detail-row">
                    <RowLabel>Создан</RowLabel>
                    <span className="detail-row__value">{formatDate(order.createdAt, pickupTz)}</span>
                  </div>
                  {order.status === OrderStatusEnum.CANCELED && (
                    <div className="detail-row">
                      <RowLabel>Причина отмены</RowLabel>
                      <span className="detail-row__value">{order.cancellationComment || 'Не указана'}</span>
                    </div>
                  )}
                  <div className="order-history-block">
                    {historyLoading ? (
                      <Loader size="sm" />
                    ) : statusHistory && statusHistory.length > 0 ? (
                      <div className="order-history-list">
                        {statusHistory.map((entry) => (
                          <div key={entry.id} className="order-history-item">
                            {formatDate(entry.createdAt, pickupTz)} — {getStatusLabel(entry.status)}
                            {entry.changedBy ? ` (${changedByLabels[entry.changedBy] ?? entry.changedBy})` : ''}
                            {entry.comment ? `: ${entry.comment}` : ''}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="order-history-item">История пуста</div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}

      <DoubleCheckModal
        isOpen={cancelModalOrderId != null}
        onClose={closeCancelModal}
        title="Отменить заказ доставки?"
        message={
          cancelModalOrderId != null ? (
            <>
              Вы уверены, что хотите отменить заказ доставки в Достависте?
              {cancelModalOrderNumber != null && <> Заказ {cancelModalOrderNumber} будет отменён.</>}
            </>
          ) : null
        }
        firstButtonText="Нет"
        secondButtonText="Да, отменить"
        secondButtonAction={confirmCancelDelivery}
        isLoading={cancelLoading}
      />

      <DoubleCheckModal
        isOpen={returnModalOrderId != null}
        onClose={closeReturnModal}
        title="Заказать возврат?"
        message="Вы уверены, что хотите включить возврат на первую точку в Достависте?"
        firstButtonText="Нет"
        secondButtonText="Да, вернуть"
        secondButtonAction={confirmOrderReturn}
        isLoading={returnLoadingOrderId != null}
      />

      <ErrorModal
        isOpen={orderCannotBeCanceledModalOpen}
        onClose={() => setOrderCannotBeCanceledModalOpen(false)}
        errorMessage="Заказ нельзя отменить. Свяжитесь со службой поддержки."
      />
    </div>
  );
}
